import { parseDate } from "@internationalized/date";
import {
  CalendarBlank,
  CaretLeft,
  CaretRight,
  CrosshairSimple,
  FlagBanner,
  SidebarSimple,
} from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import type { ItemLite, MilestoneView, QueryWarning } from "@plannello/shared";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
  Button as AriaButton,
  Calendar,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  Dialog,
  Heading,
} from "react-aria-components";
import { useSetViewSearch, type ViewSearch } from "@/components/items/viewSearch";
import { Button, Callout, InlineError, Popover, RowSkeleton } from "@/components/ui";
import { usePermissions } from "@/components/views/api";
import { rowsLabel, Segmented, zoomLabel } from "@/components/views/DisplayOptions";
import { parseTimelineRows, parseZoom, TIMELINE_ROWS, TIMELINE_ZOOMS } from "@/components/views/viewState";
import { useCatalog } from "@/data/CatalogProvider";
import { useItems } from "@/data/items";
import { updateItems } from "@/data/mutations";
import type { Product } from "@/data/types";
import { cx } from "@/lib/cx";
import { useIsMobile } from "@/lib/media";
import { createPref, usePref } from "@/lib/prefs";
import { milestoneLabel } from "../milestoneOrder";
import { updateMilestone } from "../milestones/api";
import { buildRows, DAY_WIDTH, dayOf, itemSpan, todayDay, visibleRange } from "./model";
import { SetDatesPopover } from "./SetDatesPopover";
import { TimelineAgenda } from "./TimelineAgenda";
import { LEAD_DAYS, TimelineCanvas } from "./TimelineCanvas";
import { UnscheduledPanel } from "./UnscheduledPanel";

const unscheduledPref = createPref<boolean>("pt-timeline-unscheduled", true, (r) =>
  r === "true" ? true : r === "false" ? false : undefined,
);

/** Collapsed rows survive layout switches within a session (per product and mode). */
const collapsedMemory = new Map<string, Set<string>>();

/**
 * Timeline layout of a product: rows by epics, all dated items or milestones; bars from start and
 * due dates; milestones as markers; today line; an Unscheduled panel for undated items. Zoom,
 * rows and sub-items are URL state like every other display option. Phones get an agenda list.
 */
export function TimelineView({
  product,
  search,
  header,
}: {
  product: Product;
  search: ViewSearch;
  header: (r: { count: number; warnings: QueryWarning[]; isSearching: boolean }) => ReactNode;
}) {
  const catalog = useCatalog();
  const navigate = useNavigate();
  const setSearch = useSetViewSearch();
  const perms = usePermissions();
  const isMobile = useIsMobile();
  const productIds = useMemo(() => [product.id], [product.id]);
  const result = useItems(search.q ?? "", productIds);
  const mode = parseTimelineRows(search.rows);
  const zoom = parseZoom(search.zoom);
  const showSubItems = search.subs !== "off";
  const memKey = `${product.id}:${mode}`;
  const [collapsed, setCollapsed] = useState<Set<string>>(() => collapsedMemory.get(memKey) ?? new Set());
  const [panelOpen, setPanelOpen] = usePref(unscheduledPref);
  const [announcement, setAnnouncement] = useState("");
  const [dates, setDates] = useState<{ item: ItemLite; open: boolean } | null>(null);
  const [msDate, setMsDate] = useState<{ milestone: MilestoneView; open: boolean } | null>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const canManageMilestones = perms.product("milestone.manage", product.id);

  useEffect(() => {
    setCollapsed(collapsedMemory.get(memKey) ?? new Set());
  }, [memKey]);
  useEffect(() => {
    collapsedMemory.set(memKey, collapsed);
  }, [memKey, collapsed]);

  const built = useMemo(
    () =>
      catalog.ready
        ? buildRows(result.items, catalog, { mode, showSubItems, collapsed, all: result.all.items })
        : { rows: [], unscheduled: [], scheduled: 0 },
    [result.items, result.all.items, catalog, mode, showSubItems, collapsed],
  );
  const markers = useMemo(
    () =>
      catalog.milestones
        .filter((ms) => ms.productId === product.id && ms.targetDate && ms.status === "open")
        .sort((a, b) => (a.targetDate! < b.targetDate! ? -1 : 1)),
    [catalog.milestones, product.id],
  );
  const range = useMemo(
    () =>
      visibleRange(
        built.rows,
        zoom,
        markers.map((ms) => dayOf(ms.targetDate!)),
      ),
    [built.rows, zoom, markers],
  );
  const byId = result.all.byId;
  // Done and canceled work does not need dates: Unscheduled lists what is still open.
  const unscheduled = useMemo(
    () =>
      built.unscheduled.filter((i) => {
        const c = catalog.statusById.get(i.statusId)?.category;
        return c !== "completed" && c !== "canceled";
      }),
    [built.unscheduled, catalog],
  );
  const anyDated = useMemo(
    () =>
      built.rows.some((r) =>
        r.kind === "item" ? !!itemSpan(r.item) || !!r.rollup || r.anchor !== null : !!r.milestone?.targetDate,
      ),
    [built.rows],
  );

  const setZoom = (z: string) => setSearch({ zoom: z === "months" ? undefined : z });
  const scrollToToday = () => {
    const sc = scrollRef.current;
    if (!sc) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const lead = LEAD_DAYS[zoom];
    sc.scrollTo({
      left: Math.max(0, (todayDay() - lead - range.start) * DAY_WIDTH[zoom]),
      behavior: reduce ? "auto" : "smooth",
    });
  };

  const toggle = (id: string) =>
    setCollapsed((c) => {
      const next = new Set(c);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const writeDates = (item: ItemLite, start: string | null, due: string | null) => {
    void updateItems([item], () => ({ startDate: start, dueDate: due }), {
      undoTitle: start ? m.tl_dates_changed({ key: item.key }) : m.tl_dates_cleared({ key: item.key }),
    });
  };

  const open = (item: ItemLite) => void navigate({ to: "/$itemKey", params: { itemKey: item.key } });
  const peek = (item: ItemLite) => setSearch({ peek: search.peek === item.key ? undefined : item.key });
  const openDates = (item: ItemLite, anchor: HTMLElement) => {
    anchorRef.current = anchor;
    setDates({ item, open: true });
  };

  const headerNode = header({
    count: result.items.length,
    warnings: result.warnings,
    isSearching: result.isSearching,
  });

  const toolbar = (
    <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 px-5 pb-3 sm:px-8">
      <div className="max-w-md min-w-0 flex-1 basis-64">
        <Segmented
          label={m.tl_rows()}
          hideLabel
          value={mode}
          options={TIMELINE_ROWS.map((r) => ({ id: r, label: rowsLabel(r) }))}
          onChange={(r) => setSearch({ rows: r === "epics" ? undefined : r })}
        />
      </div>
      <span className="flex-1" />
      {isMobile ? null : (
        <>
          <div className="w-60">
            <Segmented
              label={m.tl_zoom()}
              hideLabel
              value={zoom}
              options={TIMELINE_ZOOMS.map((z) => ({ id: z, label: zoomLabel(z) }))}
              onChange={setZoom}
            />
          </div>
          <Button variant="ghost" size="sm" onPress={scrollToToday}>
            <CrosshairSimple size={14} aria-hidden="true" />
            {m.tl_today()}
          </Button>
          <Button
            variant={panelOpen ? "secondary" : "ghost"}
            size="sm"
            aria-pressed={panelOpen}
            onPress={() => setPanelOpen(!panelOpen)}
          >
            <SidebarSimple size={14} aria-hidden="true" className="-scale-x-100" />
            {m.tl_unscheduled()}
            <span className="text-fg-subtle tnum">{unscheduled.length}</span>
          </Button>
        </>
      )}
    </div>
  );

  let body: ReactNode;
  if ((result.isPending || !catalog.ready) && result.items.length === 0) body = <RowSkeleton rows={8} />;
  else if (result.isError && result.items.length === 0)
    body = (
      <div className="px-5 py-4 sm:px-8">
        <InlineError message={m.error_network()} onRetry={result.refetch} />
      </div>
    );
  else if (isMobile)
    body = <TimelineAgenda items={result.items} unscheduled={unscheduled.length} catalog={catalog} onOpen={open} />;
  else
    body = (
      <div className="flex min-h-0 flex-1 border-t border-line">
        <div className="flex min-w-0 flex-1 flex-col">
          {built.rows.length === 0 || !anyDated ? (
            <EmptyRows
              reason={built.rows.length === 0 ? "no-rows" : "no-dates"}
              mode={mode}
              hasItems={result.items.length > 0}
              hasMilestones={catalog.milestones.some((ms) => ms.productId === product.id)}
              hasDated={built.scheduled > 0}
              onMode={(r) => setSearch({ rows: r === "epics" ? undefined : r })}
            />
          ) : null}
          <TimelineCanvas
            rows={built.rows}
            range={range}
            zoom={zoom}
            catalog={catalog}
            markers={markers}
            label={m.tl_label({ product: product.name, mode: rowsLabel(mode) })}
            peekKey={search.peek ?? null}
            onToggle={toggle}
            onDates={(item, s, d) => writeDates(item, s, d)}
            onSetDates={openDates}
            onSetMilestoneDate={
              canManageMilestones
                ? (ms, anchor) => {
                    anchorRef.current = anchor;
                    setMsDate({ milestone: ms, open: true });
                  }
                : undefined
            }
            onOpen={open}
            onPeek={peek}
            onMilestone={(ms) =>
              void navigate({
                to: "/p/$productKey/milestones/$milestoneId",
                params: { productKey: product.key, milestoneId: ms.id },
              })
            }
            onAnnounce={setAnnouncement}
            lookup={(id) => byId.get(id)}
            scrollRef={scrollRef}
          />
        </div>
        {panelOpen ? (
          <UnscheduledPanel
            items={unscheduled}
            catalog={catalog}
            onSetDates={openDates}
            onPeek={peek}
            onClose={() => setPanelOpen(false)}
          />
        ) : null}
      </div>
    );

  const current = dates ? (byId.get(dates.item.id) ?? dates.item) : null;
  return (
    <>
      {headerNode}
      {toolbar}
      {body}
      <SetDatesPopover
        item={current}
        triggerRef={anchorRef}
        isOpen={!!dates?.open}
        onOpenChange={(o) => setDates((d) => (d ? { ...d, open: o } : d))}
        onChange={(s, d) => {
          if (current) writeDates(current, s, d);
        }}
      />
      {msDate ? (
        <MilestoneDatePopover
          milestone={catalog.milestoneById.get(msDate.milestone.id) ?? msDate.milestone}
          triggerRef={anchorRef}
          isOpen={msDate.open}
          onOpenChange={(o) => setMsDate((x) => (x ? { ...x, open: o } : x))}
          onChange={(d) =>
            void updateMilestone(
              product.key,
              msDate.milestone,
              { targetDate: d },
              {
                undoTitle: m.tl_target_changed({ name: milestoneLabel(msDate.milestone) }),
              },
            ).catch(() => undefined)
          }
        />
      ) : null}
      <div aria-live="polite" className="sr-only">
        {announcement}
      </div>
    </>
  );
}

/** Nothing to draw: say why and offer the modes that will show something. */
function EmptyRows({
  reason,
  mode,
  hasItems,
  hasMilestones,
  hasDated,
  onMode,
}: {
  reason: "no-rows" | "no-dates";
  mode: string;
  hasItems: boolean;
  hasMilestones: boolean;
  /** Some item of the view has dates (then "All dated" shows something). */
  hasDated: boolean;
  onMode: (r: string) => void;
}) {
  if (!hasItems) return null;
  const epics = reason === "no-rows" && mode === "epics";
  const actions = (
    <>
      {mode !== "milestone" && hasMilestones ? (
        <Button variant="secondary" size="sm" onPress={() => onMode("milestone")}>
          <FlagBanner size={14} aria-hidden="true" />
          {m.tl_show_milestones()}
        </Button>
      ) : null}
      {mode !== "all" && hasDated ? (
        <Button variant="ghost" size="sm" onPress={() => onMode("all")}>
          <CalendarBlank size={14} aria-hidden="true" />
          {m.tl_show_all()}
        </Button>
      ) : null}
    </>
  );
  return (
    <div className="shrink-0 px-5 pt-3 pb-3 sm:px-8">
      <Callout title={epics ? m.tl_empty_epics_title() : m.tl_empty_dates_title()} actions={actions}>
        {epics
          ? m.tl_empty_epics_body()
          : mode === "milestone"
            ? m.tl_empty_dates_body_milestones()
            : m.tl_empty_dates_body()}
      </Callout>
    </div>
  );
}

const navButton =
  "flex size-7 items-center justify-center rounded-control text-fg-muted outline-hidden data-hovered:bg-hover data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus";

function MilestoneDatePopover({
  milestone,
  triggerRef,
  isOpen,
  onOpenChange,
  onChange,
}: {
  milestone: MilestoneView;
  triggerRef: React.RefObject<HTMLElement | null>;
  isOpen: boolean;
  onOpenChange: (o: boolean) => void;
  onChange: (date: string | null) => void;
}) {
  const label = m.ms_set_target({ name: milestoneLabel(milestone) });
  return (
    <Popover
      triggerRef={triggerRef}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      placement="bottom start"
      className="p-0"
    >
      <Dialog aria-label={label} className="flex flex-col gap-2 p-3 outline-none">
        {({ close }) => (
          <>
            <p className="text-xs font-medium text-fg">{label}</p>
            <Calendar
              aria-label={label}
              defaultValue={milestone.targetDate ? parseDate(milestone.targetDate) : null}
              onChange={(d) => {
                onChange(d.toString());
                close();
              }}
              className="w-64"
            >
              <header className="mb-2 flex items-center gap-1">
                <AriaButton slot="previous" className={navButton}>
                  <CaretLeft size={14} aria-hidden="true" />
                </AriaButton>
                <Heading className="flex-1 text-center text-sm font-medium text-fg" />
                <AriaButton slot="next" className={navButton}>
                  <CaretRight size={14} aria-hidden="true" />
                </AriaButton>
              </header>
              <CalendarGrid className="w-full border-collapse">
                <CalendarGridHeader>
                  {(day) => (
                    <CalendarHeaderCell className="pb-1 text-2xs font-medium text-fg-subtle">{day}</CalendarHeaderCell>
                  )}
                </CalendarGridHeader>
                <CalendarGridBody>
                  {(date) => (
                    <CalendarCell
                      date={date}
                      className={cx(
                        "m-px flex size-8 cursor-default items-center justify-center rounded-control text-sm text-fg outline-none tnum",
                        "data-hovered:bg-hover data-outside-month:text-fg-subtle data-outside-month:opacity-50",
                        "data-selected:bg-accent data-selected:text-accent-fg data-focus-visible:outline-2 data-focus-visible:outline-solid data-focus-visible:outline-focus",
                        "data-today:font-semibold data-today:underline data-today:underline-offset-4",
                      )}
                    />
                  )}
                </CalendarGridBody>
              </CalendarGrid>
            </Calendar>
          </>
        )}
      </Dialog>
    </Popover>
  );
}
